/** @file Shared compile pipeline: compileDoc runs it with direct stages, recompile with cached ones. */
import { entriesByName } from '@pose2d/format/doc/entriesByName';
import { FORMAT_VERSION } from '@pose2d/format/formatVersion';
import type { Doc, DocIndex, Pose2DJson } from '@pose2d/format/types';
import type { TextureLayout } from '../textureLayout';
import { VERSION } from '../version';
import { attachSkins } from './attachSkins';
import { compileAtlas } from './compileAtlas';
import { compileEvents } from './compileEvents';
import { compileIk } from './compileIk';
import { compileMixDefaults } from './compileMixDefaults';
import { compileSkins } from './compileSkins';
import { compileSlots } from './compileSlots';
import type { CompileOutput, CompileStages } from './compileStages';
import { pruneWarnings } from './pruneWarnings';

/**
 * compileDoc's orchestration. Every compile-stage call goes through `stages` (skinning too, via attachSkins); it never
 * mutates its input, a stage result or an index map handed to a stage (recompile shares them across results).
 */
export function runCompile(doc: Doc, index: DocIndex, layout: TextureLayout, stages: CompileStages): CompileOutput {
  const { bones, boneIndex } = stages.bones(doc, index);
  const { slots, slotIndex } = compileSlots(doc, boneIndex);
  const { ik, ikIndex } = compileIk(doc, boneIndex);
  const sockets = stages.sockets(doc, index, boneIndex);
  const compiled = stages.attachments(doc, index, boneIndex);
  const { attachmentIndex } = compiled;
  const attachments = attachSkins(doc, compiled, boneIndex, stages.skinning);
  const { skins, skinIndex, defaultSkin } = compileSkins(doc, slotIndex, attachmentIndex);
  const { events, eventIndex } = compileEvents(doc);
  const animationIndex = new Map(entriesByName(doc.animations).map(([id], i) => [id, i]));
  const animations = stages.animations(doc, index, { boneIndex, slotIndex, ikIndex, attachmentIndex, eventIndex });
  const directions = stages.directions(doc, index, { skinIndex, animationIndex, slotIndex });
  const mixDefaults = compileMixDefaults(doc);
  const updateOrder = stages.updateOrder(doc, index, boneIndex, ikIndex);
  const json: Pose2DJson = {
    format: 'pose2d.rig',
    version: FORMAT_VERSION.rig,
    generator: `pose2d-compiler ${VERSION}`,
    hash: '',
    yUp: true,
    units: { length: 'px', angle: 'deg', time: 's' },
    bones, updateOrder, slots, ik, sockets, attachments, skins, defaultSkin, events,
    ...(mixDefaults === undefined ? {} : { mixDefaults }),
    ...(directions === undefined ? {} : { directions }),
    animations,
  };
  return { json, atlas: compileAtlas(doc, layout), warnings: pruneWarnings(doc) };
}
